import {createRouter, createWebHistory, type RouteRecordRaw} from 'vue-router'
import * as Sentry from '@sentry/vue'
import {Excludeable, type Maybe} from './types'
import {useGlobalStore} from './stores/global'
import {useCompanyStore} from './stores/company'
import {useLoadingStore} from './stores/loading'
import DpaHeader from './components/DataPrivacyAgreement/DpaHeader.vue'
import RateboardFooter from './components/RateboardFooter.vue'
import RateboardHeader from './components/header/RateboardHeader.vue'
import {useUserStore} from './stores/user'
import {useNotificationsStore} from './stores/notifications'
import {isNullOrUndefined} from './utils/utils'
import versionService from './services/VersionService'
import {version as currentVersion} from '../package.json'
import CompanyPropertiesCookie from './utils/CompanyPropertiesCookie'
import CompanyService from './services/CompanyService'
import ExpiryCookie from './utils/ExpiryCookie'
import {isUpsellingVisible} from '@/views/analyse/benchmarking/isUpsellingVisible'
import AuthService from '@/services/Auth/AuthService'
import AuthorizePage from '@/views/AuthorizePage.vue'
import {checkMaintenanceMode} from './utils/maintenanceMode'
import {isMarketTrendsVisible} from '@/components/market-trends/isMarketTrendsVisible'
import {type UiVersion} from '@/composable/useNewUiEnabled'

declare global {
  interface Window {
    hj?: (command: string, value: string) => void
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/RateboardEA/login',
      redirect: '/',
      beforeEnter: () => {
        window.location.replace('/')
      },
    },
    {
      path: '/authorize',
      component: AuthorizePage,
      props({query}) {
        return {
          redirect: query.redirect,
        }
      },
    },
    {
      path: '/data-privacy-agreement',
      components: {
        default: () => import('./views/DataPrivacyAgreement.vue'),
        Header: RateboardHeader,
        Footer: RateboardFooter,
      },
    },
    {
      path: '/contract',
      components: {
        default: () => import('./views/Contract.vue'),
        Header: DpaHeader,
      },
    },
    {
      path: '/',
      name: 'dashboard',
      components: {
        default: () => import('./views/dashboard/Dashboard.vue'),
        Header: RateboardHeader,
        Footer: RateboardFooter,
      },
    },
    {
      path: '/dashboard-new',
      redirect: '/',
    },
    {
      path: '/manage',
      components: {
        default: () => import('./views/ManagePage.vue'),
        Header: RateboardHeader,
        Footer: RateboardFooter,
      },
      meta: {authorities: ['VIEWER']},
    },
    {
      path: '/admin',
      meta: {uiVersion: 'legacy'},
      children: [
        {
          path: 'hotel-administration',
          children: [
            {
              path: '',
              components: {
                default: () => import('./views/administration/HotelAdministration.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
              meta: {authorities: ['ADMIN']},
            },
            {
              path: 'edit',
              components: {
                default: () => import('./views/administration/HotelAdministrationEditor.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
              props: {
                default: (route) => ({companyId: route.query.companyId}),
              },
              meta: {authorities: ['ADMIN']},
            },
          ],
        },
        {
          path: 'forecast-control',
          children: [
            {
              path: '',
              components: {
                default: () => import('./views/administration/ForecastControl.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
              meta: {authorities: ['ADMIN']},
            },
          ],
        },
        {
          path: 'region-forecast-control',
          children: [
            {
              path: '',
              components: {
                default: () => import('./views/administration/RegionForecastControl.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
              meta: {authorities: ['ADMIN']},
            },
          ],
        },
        {
          path: '/admin/usage-analytics',
          components: {
            default: () => import('./views/administration/UsageAnalytics.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
          meta: {authorities: ['ADMIN']},
        },
        {
          path: 'rate-engine-poc',
          children: [
            {
              path: '',
              components: {
                default: () => import('./views/administration/RateEnginePOC.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
              meta: {authorities: ['ADMIN']},
            },
          ],
        },
        {
          path: 'inbound-data',
          components: {
            default: () => import('./views/administration/inboundData/InboundData.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
          meta: {authorities: ['ADMIN']},
          children: [
            {
              path: '',
              component: () => import('./views/administration/inboundData/reservationsTab/Reservations.vue'),
            },
            {
              path: 'comparison',
              component: () => import('./views/administration/inboundData/comparisonTab/ReservationsComparison.vue'),
            },
            {
              path: 'inventory',
              component: () => import('./views/administration/inboundData/inventoryTab/Inventory.vue'),
            },
            {
              path: 'blocks',
              component: () => import('./views/administration/inboundData/BlocksDeletion.vue'),
            },
            {
              path: 'room-stats',
              component: () => import('./views/administration/inboundData/InboundDataRoomStats.vue'),
            },
          ],
        },
        {
          path: 'customer-success-management',
          components: {
            default: () => import('./views/administration/CustomerSuccessManagement.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
          meta: {authorities: ['ADMIN']},
        },
        {
          path: 'users/invitations/:id',
          components: {
            default: () => import('./views/administration/userManagement/EditInvitation.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
          meta: {authorities: ['ADMIN']},
          props: {
            default: (route) => ({invitationId: parseInt(route.params.id as string)}),
          },
        },
        {
          path: 'users/:id',
          components: {
            default: () => import('./views/administration/userManagement/editUser/EditUser.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
          meta: {authorities: ['ADMIN']},
          props: {
            default: (route) => ({userId: route.params.id}),
          },
        },
        {
          path: 'users',
          meta: {authorities: ['ADMIN']},
          children: [
            {
              path: '',
              components: {
                default: () => import('./views/administration/userManagement/UserManagement.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
            },
            {
              path: 'admins',
              components: {
                default: () => import('./views/administration/userManagement/UserManagement.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
            },
          ],
        },
        {
          path: 'connectivity-stats',
          components: {
            default: () => import('./views/administration/ConnectivityStats.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
          meta: {authorities: ['ADMIN']},
        },
      ],
    },
    {
      path: '/analyse',
      children: [
        {
          path: 'market-trend',
          beforeEnter: () => {
            const companyStore = useCompanyStore()
            const globalStore = useGlobalStore()
            if (!isMarketTrendsVisible(companyStore.address, companyStore.marketTrends, globalStore.hasAuthority('ADMIN'))) {
              return {
                name: 'redirection',
                query: {status: 403},
                force: true,
                params: {pathMatch: ['analyse', 'market-trend']},
              }
            }
          },
          components: {
            default: () => import('./views/analyse/MarketTrend.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
        {
          path: 'reputation',
          components: {
            default: () => import('./views/analyse/ReputationPage.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
        {
          path: 'pickup',
          components: {
            default: () => import('./views/analyse/pickup/Pickup.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
        {
          path: 'hotel',
          components: {
            default: () => import('./views/analyse/hotel/AnalyseHotel.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
        {
          path: 'comparison',
          components: {
            default: () => import('./views/analyse/comparison/AnalyseComparison.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
        {
          path: 'benchmarking',
          beforeEnter: () => {
            const companyStore = useCompanyStore()
            return companyStore.benchmarkingActive || isUpsellingVisible(companyStore)
          },
          components: {
            default: () => {
              const companyStore = useCompanyStore()
              if (companyStore.benchmarkingActive) {
                return import('./views/benchmarking/Benchmarking.vue')
              }

              if (isUpsellingVisible(companyStore)) {
                return import('./views/analyse/benchmarking/BenchmarkingUpselling.vue')
              }
            },
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
      ],
    },
    {
      path: '/activity-log',
      components: {
        default: () => import('./views/ActivityLog.vue'),
        Header: RateboardHeader,
        Footer: RateboardFooter,
      },
    },
    {
      path: '/change-password',
      components: {
        default: () => import('./views/ChangePassword.vue'),
      },
    },
    {
      path: '/connect',
      children: [
        {
          path: 'apaleo',
          components: {
            default: () => import('./views/connect/ConnectApaleo.vue'),
          },
        },
      ],
    },
    {
      path: '/settings',
      children: [
        {
          path: 'user',
          children: [
            {
              path: '',
              components: {
                default: () => import('./views/UserSettings.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
              meta: {
                excluded: [Excludeable.COMPANY_SETUP_CHECK],
              },
            },
            {
              path: 'security',
              components: {
                default: () => import('./views/UserSettingsSecurity.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
              meta: {
                excluded: [Excludeable.COMPANY_SETUP_CHECK],
              },
            },
          ],
        },
        {
          path: 'inventory',
          meta: {authorities: ['HOTEL_ADMIN']},
          children: [
            {
              path: 'config',
              components: {
                default: () => import('./views/settings/inventory/InventoryConfiguration.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
            },
            {
              path: 'capacity',
              components: {
                default: () => import('./views/settings/inventory/InventoryCapacity.vue'),
                Header: RateboardHeader,
                Footer: RateboardFooter,
              },
            },
          ],
        },
        {
          path: 'interfaces',
          components: {
            default: () => import('./views/InterfaceConfiguration.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
        {
          path: 'segmentation',
          components: {
            default: () => import('./views/settings/Segmentation.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
        {
          path: 'rate-rounding',
          meta: {authorities: ['HOTEL_ADMIN']},
          components: {
            default: () => import('./views/settings/RateRoundingPage.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
        {
          path: 'rate-rules',
          meta: {authorities: ['HOTEL_ADMIN']},
          components: {
            default: () => import('./views/settings/RateRulePage.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
        {
          path: 'notification-rules',
          meta: {authorities: ['USER']},
          components: {
            default: () => import('./views/settings/NotificationRulePage.vue'),
            Header: RateboardHeader,
            Footer: RateboardFooter,
          },
        },
      ],
    },
    legacyIframe('/dashboard-legacy', '/legacy-ui/dashboard', {
      name: 'legacyDashboard',
      uiVersion: 'legacy',
      authorities: ['ADMIN'],
    }),
    legacyIframe('/analyse/competitors', '/legacy-ui/analyse/competitors'),
    legacyIframe('/analyse/budget', '/legacy-ui/analyse/budget'),
    legacyIframe('/settings/competitors', '/legacy-ui/settings/competitors'),
    legacyIframe('/settings/events', '/legacy-ui/settings/events'),
    legacyIframe('/settings/booking-status', '/legacy-ui/settings/bookingsettings'),
    legacyIframe('/settings/budget', '/legacy-ui/settings/budget'),
    legacyIframe('/settings/price-levels', '/legacy-ui/settings/price-levels'),
    legacyIframe('/settings/rate-plans', '/legacy-ui/settings/rate-plans'),
    legacyIframe('/settings/restrictions', '/legacy-ui/settings/restrictions'),
    legacyIframe('/settings/room-pricing', '/legacy-ui/settings/room-pricing'),
    legacyIframe('/admin/reservations', '/legacy-ui/administration/reservations'),
    legacyIframe('/admin/reports', '/legacy-ui/administration/reportsoverview'),
    legacyIframe('/admin/outbound-logs', '/legacy-ui/administration/outboundlogs'),
    {
      path: '/hotel-selection',
      redirect: '/',
    },
    {
      path: '/:pathMatch(.*)*',
      children: [
        {
          path: '/:pathMatch(.*)*',
          name: 'redirection',
          components: {
            default: () => import('./views/PageNotFound.vue'),
            Header: RateboardHeader,
          },
          props: {
            default: (route) => ({
              redirection: true,
              status: route.query.status,
            }),
          },
          meta: {excluded: [Excludeable.ALL]},
        },
        {
          path: '/:pathMatch(.*)*',
          name: 'static',
          components: {
            default: () => import('./views/PageNotFound.vue'),
          },
          props: {
            default: (route) => ({
              redirection: false,
              status: route.query.status ?? '500',
            }),
          },
          meta: {excluded: [Excludeable.ALL]},
        },
        {
          path: '/:pathMatch(.*)*',
          name: 'staticWithHeader',
          components: {
            default: () => import('./views/PageNotFound.vue'),
            Header: RateboardHeader,
          },
          props: {
            default: (route) => ({
              redirection: false,
              status: route.query.status ?? '500',
            }),
          },
          meta: {excluded: [Excludeable.COMPANY_STORE]},
        },
      ],
    },
  ],
})

router.beforeEach(async (to) => {
  if (checkMaintenanceMode()) {
    return false
  }

  if (to.path === '/authorize') {
    return true
  }

  if (!ExpiryCookie.isAuthenticated()) {
    return {
      path: '/authorize',
      query: {redirect: to.path === '/' ? null : to.path},
    }
  }

  if (ExpiryCookie.isExpired()) {
    try {
      await AuthService.refresh()
    } catch {
      return {
        path: '/authorize',
        query: {redirect: to.path === '/' ? null : to.path},
      }
    }
  }

  const companyStore = useCompanyStore()
  const globalStore = useGlobalStore()
  const loadingStore = useLoadingStore()
  const userStore = useUserStore()

  if (isRequired(Excludeable.GLOBAL_STORE, to.meta.excluded)) {
    try {
      await globalStore.initialize()
    } catch (error) {
      console.error('GlobalStore initialization failed')
      console.error(error)
      return {
        name: 'static',
        force: true,
        params: {pathMatch: to.path.split('/').slice(1)},
      }
    }

    if (globalStore.passwordResetActive) {
      if (!to.fullPath.includes('/change-password')) {
        window.location.replace('/change-password')
      }
      return
    }

    if (to.name !== 'staticWithHeader' && to.name !== 'static') {
      if (globalStore.hasAuthority('PMS_ADMIN') && to.path === '/') {
        window.location.replace('/admin/reservations')
      }
    }

    if (to.meta.authorities && !globalStore.hasAuthority(...to.meta.authorities)) {
      return {
        name: 'redirection',
        query: {status: 403},
        force: true,
        params: {pathMatch: to.path.split('/').slice(1)},
      }
    }

    if (isRequired(Excludeable.USER_STORE, to.meta.excluded)) {
      try {
        await userStore.initialize()
      } catch (error) {
        console.error('UserStore initialization failed')
        console.error(error)
        return {
          name: 'static',
          force: true,
          params: {pathMatch: to.path.split('/').slice(1)},
        }
      }
    }

    setSentryUserContext(userStore.id, userStore.username)
  }

  if (isRequired(Excludeable.COMPANY_PROPERTIES, to.meta.excluded)) {
    if (!CompanyPropertiesCookie.isSet()) {
      const defaultCompany = !isNullOrUndefined(userStore.settings.defaultCompanyId)
        ? await CompanyService.fetchSingle(userStore.settings.defaultCompanyId).catch(() => undefined)
        : undefined

      if (!isNullOrUndefined(userStore.settings.defaultCompanyId) && defaultCompany === undefined) {
        userStore.updateUserSettings({...userStore.settings, defaultCompanyId: null})
      }

      const company = defaultCompany ?? (await CompanyService.fetchFirst().catch(() => undefined))

      if (company === undefined) {
        console.error('No company assigned to user!')
        return {
          name: 'static',
          query: {status: 'NoActiveHotel'},
          force: true,
          params: {pathMatch: to.path.split('/').slice(1)},
        }
      }

      CompanyPropertiesCookie.set(company)
      CompanyService.updateLastUsedDate(company.id)
    }
  }

  if (isRequired(Excludeable.COMPANY_STORE, to.meta.excluded)) {
    try {
      await companyStore.initialize()
    } catch (error) {
      console.error('CompanyStore initialization failed')
      console.error(error)
      return {
        name: 'static',
        force: true,
        params: {pathMatch: to.path.split('/').slice(1)},
      }
    }

    if (isRequired(Excludeable.GLOBAL_STORE, to.meta.excluded)) {
      if (!globalStore.hasAuthority('ADMIN', 'PMS_ADMIN')) {
        if (isRequired(Excludeable.COMPANY_SETUP_CHECK, to.meta.excluded)) {
          if (!companyStore.dpaAccepted) {
            if (!to.fullPath.includes('/contract')) {
              window.location.href = '/contract'
            }
            return
          }
        }
      }
    }

    setSentryCompanyContext(companyStore.id, companyStore.name)
  }

  await useNotificationsStore()
    .initialize()
    .catch((e) => console.error(`Initializing notification store failed: ${e}`))

  if (await isVersionUpdated()) {
    console.info('New version detected')
    window.location.assign(addReloadKey(to.path, new Date().getTime()))
  }

  loadingStore.isLegacyLoading = !!to.meta.legacy
})

router.afterEach((to) => {
  window.hj?.('stateChange', to.fullPath)
})

function addReloadKey(path: string, rid: number) {
  return path.includes('?') ? `${path}&rid__=${rid}` : `${path}?rid__=${rid}`
}

interface LegacyIframeOptions {
  authorities?: string[]
  name?: string
  uiVersion?: UiVersion
}

function legacyIframe(path: string, src: string, options: LegacyIframeOptions = {}): RouteRecordRaw {
  return {
    path: path,
    name: options.name,
    components: {
      default: () => import('./views/IFrameView.vue'),
      Header: RateboardHeader,
    },
    props: {
      default: {src: src},
    },
    meta: {
      authorities: options.authorities,
      legacy: true,
      uiVersion: options.uiVersion,
    },
  }
}

function isRequired(excludeable: Excludeable, excluded?: Excludeable[]) {
  return !(excluded && (excluded?.includes(Excludeable.ALL) || excluded?.includes(excludeable)))
}

async function isVersionUpdated() {
  const fetchedVersion = (await versionService.getVersion()).version
  if (fetchedVersion === undefined) {
    console.error('fetchedVersion undefined')
    return false
  }
  if (fetchedVersion !== currentVersion) {
    console.log(`current version ${currentVersion}`)
    console.log(`fetched version ${fetchedVersion}`)
    return true
  }
  return false
}

function setSentryCompanyContext(companyId: Maybe<number>, companyName: Maybe<string>) {
  Sentry.setContext('company', {
    id: companyId,
    name: companyName,
  })
  Sentry.setTag('companyId', companyId)
}

function setSentryUserContext(userId: Maybe<number>, userName: Maybe<string>) {
  Sentry.setUser({
    id: userId?.toString() ?? undefined,
    username: userName ?? undefined,
  })
  Sentry.setTag('username', userName)
}
export default router
